import cloneDeep from "clone-deep"
import { AnyAction as iAnyAction } from "redux"

import { actionTypesForResource } from "./constants"
import { extractLaborRatesFromRateTables } from "../rate-tables/utils"
import {
    iKeyedResourcePaginationState,
    iMutableEntityState,
    iMutablePaginationState,
    iMutableLoadingState,
    iResourcePaginationState,
    tCachedResourceName,
    eEntityActionType,
    tEntityState,
    tPaginateArgs,
    tPaginationState,
    tLoadingState,
    tCachedEntityStateKey,
} from "./types"
import { emptyEntityState } from "../common/constants"

// Constants ------------------------------------------------------------------

// Actions that indicate cache invalidation should occur
export const CACHE_INVALIDATING_ACTIONS = [
    "INVALIDATE_CACHE",
    "SOURCE_DATA_ADDED",
    "SOURCE_DATA_UPDATED",
    "SOURCE_DATA_DELETED",
]

/**
 * All cached resource names, derived from actionTypesForResource keys.
 * This ensures the array stays in sync with tCachedResourceName type.
 */
export const CACHED_RESOURCE_NAMES = Object.keys(actionTypesForResource) as tCachedResourceName[]

// A listing of entity types (and their related entities, if any) for cache
// invalidation purposes.
//
// When an entity's cache is invalidated, we also need to invalidate the cache
// for any related entities. E.g. if we add a cost code, the project it's
// associated with in the cache will be stale because it won't reflect the
// relationship between the new cost code and itself.
//
// EVERY CACHED ENTITY MUST HAVE AN ENTRY HERE. If it has no relationships
// to other entities, assign it an empty array.
const entityRelationships: { [key in tCachedEntityStateKey]: tCachedEntityStateKey[] } = {
    cohorts: ["employees"],
    cohortEmployees: ["cohorts"],
    companyAbsenceTypes: [],
    companyFormSchemas: [],
    companyFormStores: [],
    companyGroups: ["projects", "workRuleSets"],
    companyStartStopTypes: [],
    costCodes: ["projects"],
    costItems: ["projects"],
    changeOrders: ["projects"],
    companyCrewTypes: ["costCodeControls"],
    costCodeControls: ["companyCrewTypes"],
    employees: [],
    employeeTrades: [],
    employeeClassifications: [],
    projectEmployees: ["projects"],
    guestFormShares: ["companyFormStores"],
    employeeSchemas: [],
    equipment: [],
    projectEquipment: ["projects"],
    projects: [
        "companyGroups",
        "costCodes",
        "projectMaterials",
        "projectEquipment",
        "projectEmployees",
        "costItems",
        "changeOrders",
        "workRuleSets",
    ],
    companies: [],
    materials: [],
    projectMaterials: ["projects"],
    schemaStatusNames: [],
    analyticsDashboards: [],
    companyTrades: [],
    companyClassifications: ["companyTrades"],
    picklistItems: [],
    timekeepingStatuses: [],
    timelineEntryVersions: [],
    workShifts: [],
    workRuleSets: ["projects", "companyGroups"],
    // Because laborRates are immutable, that cache shouldn't need to be invalidated when rateTable data is updated.
    rateTables: [],
    // These laborRates are immutable, so they shouldn't ever be individually changing
    // to warrant a cache invalidation elsewhere.
    laborRates: [],
    // LaborRateMatch records are immutable, so no invalidation relationships needed.
    laborRateMatches: [],
}

// Utility functions ----------------------------------------------------------

/**
 * Invalidate a resource in entities, and any resources that are related to it
 */
export const _invalidateEntityCache = (state: tEntityState, resourceName: tCachedResourceName): tEntityState => {
    const newState = cloneDeep(state) as iMutableEntityState
    newState[resourceName].invalidate = true

    const relatedEntities = entityRelationships[resourceName] as Array<tCachedResourceName>
    relatedEntities.map(relatedEntity => {
        newState[relatedEntity].invalidate = true
    })

    return newState
}

/**
 * Invalidate a resource in pagination, and any resources that are related to it
 */
export const _invalidatePaginationCache = (
    state: tPaginationState,
    resourceName: tCachedResourceName
): tPaginationState => {
    const newState = cloneDeep(state) as iMutablePaginationState
    for (const key in newState[resourceName]) {
        newState[resourceName][key].invalidate = true
    }

    const relatedEntities = entityRelationships[resourceName] as Array<tCachedResourceName>
    relatedEntities.map(relatedEntity => {
        for (const relatedKey in newState[relatedEntity]) {
            newState[relatedEntity][relatedKey].invalidate = true
        }
    })

    return newState
}

/**
 * Update the entities state in response to an action with entities in its
 * payload
 */
export const _updateEntityState = (action: iAnyAction, state: tEntityState): tEntityState => {
    const newState = cloneDeep(state) as iMutableEntityState

    // Add new entities to the cache and reset the invalidation states for the
    // relevant resource types
    const newEntities =
        action.payload && action.payload && action.payload.response ? action.payload.response.entities || {} : {}

    const resourceTypes = Object.keys(state) as Array<tCachedResourceName>

    const wa8985WorkRulesEnabled = action.payload?.wa8985WorkRulesEnabled === true

    for (const resourceType of resourceTypes) {
        if (newEntities[resourceType]) {
            if (state[resourceType].invalidate) {
                newState[resourceType] = {
                    objects: newEntities[resourceType],
                    invalidate: false,
                }
            } else {
                newState[resourceType] = {
                    objects: {
                        ...state[resourceType].objects,
                        ...newEntities[resourceType],
                    },
                    invalidate: false,
                }
            }
        } else if (!wa8985WorkRulesEnabled) {
            // When WA-8985 is off: clear invalidate for resource types absent from the response (legacy behavior).
            // When WA-8985 is on: preserve invalidate until that resource is actually refreshed.
            newState[resourceType].invalidate = false
        }
    }

    return newState as tEntityState
}

// Cached Entities ------------------------------------------------------------

const initialEntityState: tEntityState = {
    ...emptyEntityState,
}

/**
 * Reducer that updates the entity cache in response to ANY action with
 * response.entities.
 */
export const entities = (state: tEntityState = initialEntityState, action: iAnyAction): tEntityState => {
    // Perform any necessary cache invalidation
    if (CACHE_INVALIDATING_ACTIONS.includes(action.type) && action.payload) {
        const resourceName = action.payload.entityType
            ? action.payload.entityType
            : (Object.keys(action.payload)[0] as tCachedResourceName)

        if (CACHED_RESOURCE_NAMES.includes(resourceName)) {
            return _invalidateEntityCache(state, resourceName)
        }
    }

    if (action.payload && action.payload.response && action.payload.response.entities) {
        let newState = _updateEntityState(action, state)

        // Extract labor rates from rate tables when they're updated
        if (action.payload.response.entities.rateTables) {
            const extractedLaborRates = extractLaborRatesFromRateTables(newState.rateTables.objects)
            const newLaborRatesObjects = state.laborRates.invalidate
                ? extractedLaborRates
                : { ...newState.laborRates.objects, ...extractedLaborRates }
            newState = {
                ...newState,
                laborRates: {
                    objects: newLaborRatesObjects,
                    invalidate: false,
                },
            } as tEntityState
        }

        return newState
    }

    return state
}

// Error Messages -------------------------------------------------------------

/**
 * Reducer that manages error messages related to cached data
 */
export const cacheError = (state: null | string = null, action: iAnyAction): null | string => {
    const { type } = action

    if (type === "RESET_ERROR_MESSAGE") {
        return null
    } else if (Object.values(eEntityActionType).includes(type) && action.error) {
        const payload = action.payload || {}
        return payload.error ? `${payload.error}` : "Unknown cache error"
    }

    return state
}

// Pagination -----------------------------------------------------------------

/**
 * Reducer that manages pagination, given the action types to handle and a
 * function that extracts a key from the action that controls how the items
 * are grouped.
 *
 * For example, if the results are filtered by a particular query string, the
 * key would be the query string passed in from the action. If the results are
 * unfiltered (we're getting an entire set of entities), the key should be the
 * string 'all'.
 *
 * This isn't meant to be used directly; use the `pagination` reducer below.
 */
export const _paginate = (args: tPaginateArgs) => {
    const { types, mapActionToKey } = args

    // Update pagination state
    const [requestType, successType, finishedType, failureType] = types

    const updatePagination = (
        state: iKeyedResourcePaginationState = {
            error: null,
            invalidate: false,
            isFetching: false,
            nextPageUrl: undefined,
            pageCount: 0,
        },
        action: iAnyAction
    ) => {
        const newState = cloneDeep(state) as iKeyedResourcePaginationState

        switch (action.type) {
            case requestType:
                return {
                    ...newState,
                    error: null,
                    isFetching: true,
                }
            case successType:
                return {
                    ...newState,
                    error: null,
                    isFetching: true,
                    nextPageUrl: action.payload.response.nextPageUrl,
                    pageCount: state.pageCount + 1,
                }
            case finishedType:
                return {
                    ...newState,
                    error: null,
                    invalidate: false,
                    isFetching: false,
                }
            case failureType:
                return {
                    ...newState,
                    error: action.payload && action.payload.error ? action.payload.error : "Something went wrong.",
                    isFetching: false,
                }
            default:
                return state
        }
    }

    // Reducer logic
    return (state: iResourcePaginationState, action: iAnyAction): iResourcePaginationState => {
        // Update pagination by key
        switch (action.type) {
            case requestType:
            case successType:
            case finishedType:
            case failureType:
                const newState = cloneDeep(state) as iResourcePaginationState
                const key = mapActionToKey(action)
                const keyedResourceState = newState[key] as iKeyedResourcePaginationState
                return {
                    ...newState,
                    [key]: updatePagination(keyedResourceState, action),
                }
            default:
                return state
        }
    }
}

/**
 * Initial pagination state, derived from actionTypesForResource keys.
 * Each resource starts with an empty pagination object.
 */
const initialPaginationState = Object.keys(actionTypesForResource).reduce(
    (acc, key) => ({ ...acc, [key]: {} }),
    {} as tPaginationState
)

export const pagination = (
    state: tPaginationState = initialPaginationState,
    page_action: iAnyAction
): tPaginationState => {
    // Perform any necessary cache invalidation
    if (CACHE_INVALIDATING_ACTIONS.includes(page_action.type) && page_action.payload) {
        const resourceName = page_action.payload.entityType
            ? page_action.payload.entityType
            : (Object.keys(page_action.payload)[0] as tCachedResourceName)

        if (CACHED_RESOURCE_NAMES.includes(resourceName)) {
            return _invalidatePaginationCache(state, resourceName)
        }
    }

    return {
        // Standard pagination: each resource uses its own state slice
        ...CACHED_RESOURCE_NAMES.reduce((acc, resourceName) => {
            acc[resourceName] = _paginate({
                mapActionToKey: action => action.key,
                types: actionTypesForResource[resourceName],
            })(state[resourceName], page_action)
            return acc
        }, {} as iMutablePaginationState),
        // Non-standard pagination: define resources here that use a different pattern for pagination
        cohortEmployees: _paginate({
            mapActionToKey: action => action.key,
            types: actionTypesForResource["cohortEmployees"],
        })(state.cohorts, page_action),
        employeeTrades: _paginate({
            mapActionToKey: action => action.key,
            types: actionTypesForResource["employeeTrades"],
        })(state.employees, page_action),
        employeeClassifications: _paginate({
            mapActionToKey: action => action.key,
            types: actionTypesForResource["employeeClassifications"],
        })(state.employees, page_action),
        timekeepingStatuses: _paginate({
            mapActionToKey: action => action.key,
            types: actionTypesForResource["timekeepingStatuses"],
        })(state.workShifts, page_action),
        timelineEntryVersions: _paginate({
            mapActionToKey: action => action.key,
            types: actionTypesForResource["timelineEntryVersions"],
        })(state.workShifts, page_action),
    }
}

// Entity Loading State -------------------------------------------------------

const initialEntityLoadingState = Object.keys(actionTypesForResource).reduce(
    (acc, key) => ({ ...acc, [key]: { isLoading: false, error: null } }),
    {} as tLoadingState
)

/**
 * Reducer that manages the loading state of entities. This is used to control
 * the loading spinner on the custom dashboard component.
 *
 * Although much of this information is also recorded in the pagination
 * reducer, the pagination reducer keys the loading state for each entity by
 * its filters/query string. Since the custom dashboard has no insight into
 * the specific query for, say, cost codes are being generated by its row data
 * and settings file, it has no way of knowing which key to use.
 *
 * We should probably revisit the overall design of our request lifecycle
 * management. This is likely much more complicated than it needs to be.
 */
export const entitiesLoading = (
    state: tLoadingState = initialEntityLoadingState,
    action: iAnyAction
): tLoadingState => {
    const newState = cloneDeep(state) as iMutableLoadingState
    const resourceTypes = Object.keys(initialEntityLoadingState) as tCachedResourceName[]

    resourceTypes.forEach(resourceType => {
        const actionTypes = actionTypesForResource[resourceType]
        const [pendingAction, successAction, finishedAction, failedAction] = actionTypes

        switch (action.type) {
            case pendingAction:
            case successAction:
                newState[resourceType].isLoading = true
                newState[resourceType].error = null
                break
            case finishedAction:
                newState[resourceType].isLoading = false
                newState[resourceType].error = null
                break
            case failedAction:
                newState[resourceType].isLoading = false
                newState[resourceType].error = action.payload ? `${action.payload.error}` : "Something went wrong"
        }
    })

    return newState
}
